// Display names for a Feature's classification, in the two halves a reader
// needs them: the dimension's own name, and the name of each value it can hold.
//
// The authority is the ontology's own term files, `doc/reference/ontology/terms/` — `habitats/`, `water-origins/`, `observation-values/` — and
// the name is the term file's name verbatim. Where two sources in this repo
// disagree the ontology wins: `knowledge/glossary.go` and
// `knowledge/lexicon.go` spell `stormwater_or_municipal_runoff`
// "Stormwater/Municipal Runoff", but the term file is `Stormwater Municipal
// Runoff.md`, so that is the name rendered.
//
// A mechanical snake_case → sentence conversion is deliberately NOT used: it
// invents names the ontology does not have ("Stormwater or municipal runoff",
// "Recreational or ornamental water") and turns a naming decision into a
// formatting accident.
//
// A value with no term file is left as the raw token it arrived as, so an
// unnamed value shows up as unfinished rather than as a wrong name.

/** Habitat class values — the ontology's `terms/habitats/`. */
const HABITAT_CLASS_LABELS: Record<string, string> = {
	aboveground_pool: "Above Ground Pool",
	ag_grasses_or_grain: "Agricultural Grasses Grain",
	appliance: "Appliance",
	bird_bath: "Bird Bath",
	catch_basin: "Catch Basin",
	containers: "Containers Habitat",
	culvert: "Culvert",
	dairy_lagoon: "Dairy Lagoon",
	depression: "Depression",
	ditch: "Ditch",
	drain: "Drain",
	flooded_structure: "Flooded Structure",
	fountain_or_water_feature: "Fountain Water Feature",
	gutter: "Gutter",
	hot_tub: "Hot Tub",
	irrigation_standpipe: "Irrigation Standpipe",
	kid_pool: "Kid pool",
	lake: "Lake",
	log_decks: "Log Decks",
	low_point: "Low Point",
	marsh_or_wetland: "Marsh Wetland",
	misc_water_accumulation: "Misc Water Accumulation",
	orchard: "Orchard",
	pasture: "Pasture",
	plant_axil: "Plant Axil",
	plant_saucer: "Plant Saucer",
	pond: "Pond",
	rain_gutter: "Rain Gutter",
	retention_basin: "Retention Basin",
	rice: "Rice",
	river: "River",
	row_crops: "Row Crops",
	septic: "Septic",
	slough: "Slough",
	stream_or_creek: "Stream Creek",
	sump: "Sump",
	swimming_pool: "Swimming Pool",
	tail_ditch: "Tail Ditch",
	tarp_or_cover: "Tarp Cover",
	tires: "Tires Habitat",
	treehole: "Treehole",
	trough: "Trough Habitat",
	utility: "Utility Habitat",
	vine_crops: "Vine Crops",
	wastewater_treatment: "Wastewater Treatment",
	watering_bowl: "Watering Bowl",
	yard_drain: "Yard Drain",
};

/** Water origin values — the ontology's `terms/water-origins/`. */
const WATER_ORIGIN_LABELS: Record<string, string> = {
	drip_irrigation: "Drip Irrigation",
	flood_irrigation: "Flood Irrigation",
	furrow_irrigation: "Furrow Irrigation",
	industrial_runoff: "Industrial Runoff",
	irrigation_runoff: "Irrigation Runoff",
	leak: "Leak",
	permanent_natural_water: "Permanent Natural Water",
	rainwater_accumulation: "Rainwater Accumulation",
	recreational_or_ornamental_water: "Recreational Ornamental Water",
	seepage: "Seepage",
	sprinkler_irrigation: "Sprinkler Irrigation",
	stored_water: "Stored Water",
	stormwater_or_municipal_runoff: "Stormwater Municipal Runoff",
	temporary_natural_water: "Temporary Natural Water",
	wastewater_irrigation: "Wastewater Irrigation",
	wastewater_system: "Wastewater System",
	water_conveyance: "Water Conveyance",
};

/** The four single-value dimensions — the ontology's `terms/observation-values/`. */
const ORGANIC_CONTENT_LABELS: Record<string, string> = {
	clear: "Clear",
	heavy: "Heavy",
	light: "Light",
};

const FLOW_STATE_LABELS: Record<string, string> = {
	fast_flowing: "Fast-flowing",
	slow_flowing: "Slow-flowing",
	stagnant: "Stagnant",
};

const WATER_CONNECTIVITY_LABELS: Record<string, string> = {
	isolated: "Isolated",
	locally_connected: "Locally Connected",
	system_connected: "System-connected",
	unknown: "Unknown",
};

const VEGETATION_INFLUENCE_LABELS: Record<string, string> = {
	constraining: "Constraining",
	incidental: "Incidental",
	influential: "Influential",
	none: "None",
};

/** The dimension's own name, in the ontology's declared order. */
const DIMENSION_LABELS: Record<string, string> = {
	habitat_class: "Habitat class",
	water_origin: "Water origin",
	size: "Size",
	organic_content: "Organic content",
	flow_state: "Flow state",
	water_connectivity: "Water connectivity",
	vegetation_influence: "Vegetation influence",
};

/**
 * Value names per dimension. `size` is absent on purpose: its value is a
 * composed measurement the server already renders in words ("volume 45000 L,
 * area 30 m²"), not a vocabulary token, so it passes through untouched.
 */
const DIMENSION_VALUE_LABELS: Record<string, Record<string, string>> = {
	habitat_class: HABITAT_CLASS_LABELS,
	water_origin: WATER_ORIGIN_LABELS,
	organic_content: ORGANIC_CONTENT_LABELS,
	flow_state: FLOW_STATE_LABELS,
	water_connectivity: WATER_CONNECTIVITY_LABELS,
	vegetation_influence: VEGETATION_INFLUENCE_LABELS,
};

/** A classification dimension's display name; an unknown dimension is left raw. */
export function dimensionLabel(dimension: string): string {
	if (!dimension) return "Unknown";
	return DIMENSION_LABELS[dimension] ?? dimension;
}

/**
 * A classification value's display name. An empty value stays empty (the popup
 * renders its own blank); a value the ontology has not named falls back to the
 * raw token rather than to a generated name.
 */
export function dimensionValueLabel(dimension: string, value: string): string {
	if (!value) return "";
	return DIMENSION_VALUE_LABELS[dimension]?.[value] ?? value;
}
